import { useState } from 'react';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import getGqlClient from '@spotahome/marketplace-common/src/graphql/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { TOAST_TIMEOUT } from '@spotahome/marketplace-common/src/constants';
import {
  clientErrorTracker,
  getPagePath,
  trans
} from '@spotahome/soyuz/client';
import {
  Button,
  FormProvider,
  Modal,
  ModalBody,
  ModalHeader,
  showToast
} from '@spotahome/ui-library';
import ReCaptcha from '@spotahome/ui-library/src/tenant/components/ReCaptcha';
import { createZendeskTicketLogged } from '@spotahome/ui-library/src/tenant/mutations/zendesk';

import './ListingDoubtsModal.scss';

const formSchema = yup.object().shape({
  comment: yup.string().ensure().required('common.form.required-field'),
  phoneNumber: yup.string().ensure().required('common.form.required-field'),
  moveInDate: yup.string().ensure().required('common.form.required-field')
});

const ListingDoubtsModal = ({
  handleClose,
  isOpen,
  listingId,
  cityId,
  countryCode
}) => {
  const [recaptchaPassed, setRecaptchaPassed] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isErrorActive, setErrorActive] = useState(false);

  const displayThankYouToast = () => {
    showToast(
      {
        renderTitle: () => trans('listing.doubts.thank-you-toast.title'),
        renderContent: () => trans('listing.doubts.thank-you-toast')
      },
      { autoClose: TOAST_TIMEOUT }
    );
  };

  const handleFormSubmit = async values => {
    SoyuzAnalytics.sendGA4Event('send', {
      action: 'listing-doubts-attempt'
    });

    setLoading(true);
    setErrorActive(false);

    const input = {
      doubtMotive: 'trinity_doubt',
      doubtSubmotive: 'trinity_doubt',
      reason: 'listing_doubts',
      comment: values.comment.replaceAll('\n', '\\n'),
      phoneNumber: values.phoneNumber,
      moveInDate: values.moveInDate,
      role: 'tenant',
      city: cityId,
      country: countryCode,
      rentableUnitId: listingId
    };

    try {
      await createZendeskTicketLogged(getGqlClient())({ input });
      SoyuzAnalytics.sendGA4Event('send', {
        action: 'listing-doubts-success'
      });
      setLoading(false);
      handleClose();
      displayThankYouToast();
    } catch (error) {
      setLoading(false);
      clientErrorTracker.error({ error, source: 'ListingDoubts' });
      SoyuzAnalytics.sendGA4Event('send', {
        action: 'listing-doubts-error'
      });
      setErrorActive(true);
    }
  };

  const handleFormError = () => {
    setLoading(false);
  };

  const contractUsTransParts = trans('listing.doubts.modal.text').split(
    '{contactUsLink}'
  );

  return (
    <Modal
      isOpen={isOpen}
      size="medium"
      contentLabel="modal"
      ariaHideApp={false}
      onRequestClose={handleClose}
      className="listing-doubts-modal"
    >
      <ModalHeader
        label={trans('listing.doubts.model.title')}
        onRequestClose={handleClose}
        className="listing-doubts-modal__header"
      />
      <ModalBody className="listing-doubts-modal__body">
        <div className="listing-doubts-modal__text">
          <span>{contractUsTransParts[0]}</span>
          <Button
            color="link"
            onClick={getPagePath('contact-us')}
            className="listing-doubts-modal__contact-us-button"
          >
            {trans('contact-us')}
          </Button>
          <span>{contractUsTransParts[1]}</span>
        </div>
        <FormProvider
          fieldsClassName="listing-doubts-modal__fields"
          disabled={!recaptchaPassed}
          schema={formSchema}
          onSubmit={handleFormSubmit}
          onError={handleFormError}
          isLoading={isLoading}
        >
          <div className="listing-doubts-modal__form">
            <FormProvider.Input
              name="comment"
              title={trans('contact-us.field.comment.title')}
              placeholder={trans('contact-us.field.comment.placeholder')}
              type="textarea"
              rows={3}
            />

            <FormProvider.PhoneInput
              id="phoneNumber"
              name="phoneNumber"
              title={trans('contact-us.field.phoneNumber.title')}
              required
            />

            <FormProvider.Date
              name="moveInDate"
              title={trans('contact-us.field.moveInDate.title')}
              placeholder={trans('contact-us.field.moveInDate.placeholder')}
            />

            <ReCaptcha
              onChange={() => setRecaptchaPassed(true)}
              onExpired={() => setRecaptchaPassed(false)}
            />

            <FormProvider.Submit>
              {isLoading ? trans('loading') : trans('contact-us.form.submit')}
            </FormProvider.Submit>

            {isErrorActive && (
              <p className="listing-doubts-modal__error">
                {trans('contact-us.form-error')}
              </p>
            )}
          </div>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

ListingDoubtsModal.propTypes = {
  listingId: PropTypes.string.isRequired,
  cityId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  countryCode: PropTypes.string.isRequired
};

export default ListingDoubtsModal;
